import { DataPointDto } from '@/repositories';
import { IProgressStatus } from './progressStatus';
import { ITrendDataUpdater } from './trendDataUpdater';
import { CsvDownloadService } from '@/apps/timeSeriesViewer';
import moment from 'moment';

export class CsvResourcePumps {
  private _parallelRequestsLimit = 20;
  private _startDatePattern = /[~/]((?:199\d|2\d{3})\d{4})-/; // matching ~ or / then YYYYMMDD- (1990s or 2000s)
  private _endDatePattern = /_((?:199\d|2\d{3})\d{4})-/;
  private readonly aggInRequest;

  constructor(
    private _availableRequests: { configuredMetricId: string; url: string }[],
    private _trendDataUpdater: ITrendDataUpdater,
    private _progress: IProgressStatus,
    private _signal: AbortSignal,
    private _fetch: (input: RequestInfo, init?: RequestInit) => Promise<Response>,
    private _csvDownloadService: CsvDownloadService | null
  ) {
    // sort oldest to newest, by start date, with dynamic requests all grouped at the end
    this._availableRequests.sort((x, y) => {
      function singleDataPoint(url: string) {
        return /\.(?:Week|EntireRange)\./.test(url);
      }

      // Some aggregation files are dated with the beginning of the range in their filename, but the
      // data in them has a single data point timestamped at the end date, so treat their end date as their start date
      const dateXMatch = singleDataPoint(x.url) ? x.url.match(this._endDatePattern) : x.url.match(this._startDatePattern);
      const dateYMatch = singleDataPoint(y.url) ? y.url.match(this._endDatePattern) : y.url.match(this._startDatePattern);

      if (!dateXMatch) return 0;
      if (!dateYMatch) return -1;

      const dateX = parseInt(dateXMatch[1]);
      const dateY = parseInt(dateYMatch[1]);
      if (dateX > dateY) return 1;
      if (dateX < dateY) return -1;

      // now, if they have the same start date, whichever has the later end date is sorted FIRST
      const endDateXMatch = x.url.match(this._endDatePattern);
      const endDateYMatch = y.url.match(this._endDatePattern);

      if (!endDateXMatch) return 0;
      if (!endDateYMatch) return -1;

      const endDateX = parseInt(endDateXMatch[1]);
      const endDateY = parseInt(endDateYMatch[1]);
      if (endDateX > endDateY) return -1;
      if (endDateX < endDateY) return 1;
      return 0;
    });

    this.aggInRequest = this._availableRequests.some((x) => /\.(?:Month|Quarter|Yearly)\./.test(x.url));
  }

  async run(): Promise<void> {
    await delay(0);
    await Promise.all([this.availablePump()]);
  }

  async availablePump(): Promise<void> {
    while (this._availableRequests.length && !this._signal.aborted) {
      while (this._availableRequests.length && !this._signal.aborted) {
        const first = this._availableRequests.slice(0, 1)[0];
        const firstEndDate = first.url.match(this._endDatePattern);

        let sliceSize = 0;
        let sliceEndDate = null;
        for (const x of this._availableRequests) {
          const endDate = x.url.match(this._endDatePattern);
          if (!endDate || !firstEndDate) break;
          // if more recent than 2 months old, need to bundle in with the dynamics
          if (parseInt(endDate[1]) > parseInt(moment().subtract(2, 'months').format('YYYYMMDD'))) break;
          if (parseInt(endDate[1]) > parseInt(firstEndDate[1])) break;
          // if certain aggs in request and data is from current year, have to do dynamic as well
          if (this.aggInRequest) {
            const startDate = x.url.match(this._startDatePattern);
            if (startDate && parseInt(startDate[1].slice(0, 4)) == parseInt(moment().format('YYYY'))) break;
          }
          sliceSize += 1;
          sliceEndDate = endDate ? moment(`${endDate[1]}-0000 +1000`, 'YYYYMMDD-HHmm ZZ', true).utcOffset(600) : null;
        }

        if (sliceSize == 0) {
          sliceSize = this._parallelRequestsLimit;
          sliceEndDate = null;
          // console.log('dynamic batch')
          // this._availableRequests.slice(0, sliceSize).forEach(x => console.log('  ', x.url.slice(x.url.lastIndexOf('/') + 1, x.url.indexOf('.json'))))
        } else {
          // console.log('slice ', sliceSize)
          // this._availableRequests.slice(0, sliceSize).forEach(x => console.log('  ', x.url.slice(x.url.lastIndexOf('/') + 1, x.url.indexOf('.json'))))
          if (this._csvDownloadService) {
            await this._csvDownloadService.writeChunk();
            this._trendDataUpdater.clear();
          }
        }

        const nextSetOfDownloads = this._availableRequests.slice(0, sliceSize).map(async (request) => {
          const response = await this._fetch(request.url);
          const text = await response.text();
          const result = text ? JSON.parse(text).map((i: unknown) => DataPointDto.fromJS(i)) : [];
          this._trendDataUpdater.add(request.configuredMetricId, result);
          this._availableRequests.removeIfExists(request);
          this._progress.increment();
        });
        await Promise.all(nextSetOfDownloads);
        this._trendDataUpdater.update(sliceEndDate?.isValid() ? sliceEndDate : null);
      }

      // exit if aborted
      if (this._signal.aborted) {
        break;
      }
      this._progress.setMessage('Finalising');
      // putting a delay in here seems to get knockout to update the progress indicator
      await delay(0);
      this._trendDataUpdater.update();

      // write final chunk
      if (this._csvDownloadService) {
        await this._csvDownloadService.writeChunk();
        this._trendDataUpdater.clear();
      }
    }

    // make sure update is called at least once if it wasn't aborted
    if (!this._signal.aborted) this._trendDataUpdater.update();
  }
}

async function delay(ms: number): Promise<void> {
  await new Promise<void>((resolve) => setTimeout(() => resolve(), ms));
}
